<template>
  <v-dialog persistent max-width="400px" v-model="visibility" :retain-focus="false">
    <v-card v-if="visibility" class="rounded-xl pb-2">
      <v-card-title class="py-6">
        <h4 class="text-capitalize dialog-title">{{ getTitle }}</h4>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid" @submit.prevent="submit">
          <v-row no-gutters>
            <!-- Name -->
            <v-col cols="12" class="black-small-title mb-2">
              {{ t('$vuetify.__QuestionTopic.name_of', info.parent_id ? t('$vuetify.__QuestionTopic.sub_topic') : t('$vuetify.__General.question_topic')) }}
            </v-col>
            <v-col cols="12" class="mb-6">
              <v-text-field v-model="search" @keyup="setSearch" variant="solo" flat density="compact" :rules="topic_name_rules" hide-details="auto" clearable class="shadow-textfield"></v-text-field>
              <v-slide-x-transition>
                <v-card v-if="autocomplete_items.length > 0" flat class="my-2 pa-3" style="border: 1px solid rgb(0, 0, 0, 0.3)" width="100%">
                  <v-card-subtitle class="pa-0">{{ t('$vuetify.__QuestionTopic.existing_question_topic') }}:</v-card-subtitle>
                  <div style="max-height: 200px; overflow-y: auto">
                    <v-row no-gutters v-for="item in autocomplete_items" :key="item.intent_id">
                      <v-btn size="small" variant="text" color="#1b65f2" class="" style="text-transform: none !important" @click="goToIntent(item.intent_id)">{{ item.topic }}</v-btn>
                    </v-row>
                  </div>
                </v-card>
              </v-slide-x-transition>
            </v-col>

            <!-- Category -->
            <v-col v-if="!info.parent_id" cols="12">
              <v-row no-gutters class="black-small-title mb-2"> {{ t('$vuetify.__General.category') }} </v-row>
              <v-row no-gutters class="mb-6">
                <v-select
                  v-model="info.category_id"
                  :rules="category_rules"
                  :items="categories"
                  item-title="name"
                  item-value="id"
                  menu-icon="mdi-chevron-down"
                  :menu-props="{ bottom: true, offsetY: true }"
                  hide-details="auto"
                  variant="outlined"
                  density="compact"
                />
              </v-row>
            </v-col>

            <!-- Remarks -->
            <v-col cols="12" class="black-small-title mb-2">
              {{ t('$vuetify.__AI.keyword.formation') }}
            </v-col>
            <v-col cols="12" class="mb-6">
              <v-text-field variant="outlined" density="compact" hide-details="auto" v-model="info.remarks"></v-text-field>
            </v-col>
            <!-- Intent id -->
            <v-col cols="12" class="black-small-title mb-2">
              {{ t('$vuetify.__AI.keyword.intent_id') }}
            </v-col>
            <v-col cols="12" class="mb-6">
              <v-text-field variant="outlined" density="compact" hide-details="auto" v-model="info.memo"></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="pr-4">
        <v-spacer></v-spacer>
        <v-btn variant="outlined" rounded :disabled="is_submit_btn_disable" class="text-capitalize px-3 mr-1" @click="cancel">{{ t('$vuetify.__General.cancel') }}</v-btn>
        <v-btn variant="flat" rounded color="#1B65F2" :disabled="is_submit_btn_disable" :dark="!is_submit_btn_disable" class="text-capitalize px-3 mr-1" @click="submit">
          {{ t('$vuetify.__General.submit') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import debounce from 'lodash.debounce'

import { useLocale } from 'vuetify'
const { t } = useLocale()
import { useStore } from 'vuex'
const store = useStore()
import { useRouter } from 'vue-router'
const router = useRouter()

import { computed, ref, watch } from 'vue'

const search = ref(null)
const form = ref()
const valid = ref(false)
const topic_name_rules = ref([(v) => !!v || t('$vuetify.__Rules.topic_name')])
const category_rules = ref([(v) => !!v || t('$vuetify.__Rules.select_category')])

const visibility = computed(() => store.state.questionTopicMngtDialog.visibility)
const mode = computed(() => store.state.questionTopicMngtDialog.mode)
const info = computed(() => store.state.questionTopicMngtDialog.info)
const categories = computed(() => store.state.categoryMngtDialog.categories)
const autocomplete_items = computed(() => store.state.questionTopicMngtDialog.autocomplete_items)
const is_submit_btn_disable = computed(() => store.state.questionTopicMngtDialog.is_submit_btn_disable)

const getTitle = computed(() => {
  let mode = store.state.questionTopicMngtDialog.mode
  const modeText = mode === 'create' ? t('$vuetify.__General.add') : t('$vuetify.__General.edit')
  const type = info.value && info.value.parent_id ? t('$vuetify.__QuestionTopic.sub_topic') : t('$vuetify.__General.question_topic')
  return `${modeText} ${type}`
})

const setSearch = debounce(() => {
  if (search.value !== '') store.dispatch('fetchQuestionTopicAutocomplete', search.value)
  store.commit('SET_QUESTION_TOPIC_MNGT_DIALOG_AUTOCOMPLETE_ITEMS', [])
}, 500)

const goToIntent = (id) => {
  store.commit('RESET_QUESTION_TOPIC_MNGT_DIALOG')
  router.push(`/ai/keyword/question/${id}`)
}

const cancel = () => {
  store.commit('RESET_QUESTION_TOPIC_MNGT_DIALOG')
  search.value = null
}

const submit = () => {
  form.value.validate()
  if (valid.value) {
    const action = mode.value === 'create' ? 'addQuestionTopic' : 'editQuestionTopic'
    store.dispatch(action)
  }
}

watch(search, () => {
  if (search.value !== '') store.commit('SET_QUESTION_TOPIC_MNGT_DIALOG_INFO_INTENT_NAME', search.value)
})
watch(visibility, () => {
  if (visibility.value) {
    search.value = info.value.intent_name
  }
})
</script>
